import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { USE_CASES } from "@/data/useCases";

export function MarketingShell({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background text-white overflow-x-hidden grain-overlay">
      <header className="fixed top-0 inset-x-0 z-30 bg-[#050508]/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="marketing-logo">
            <img src="/nox-logo.png" alt="Nox AI" className="h-9 w-9 object-contain" />
            <div className="flex flex-col leading-none">
              <span className="font-heading font-black text-lg tracking-tight">
                Nox<span className="text-primary"> AI</span>
              </span>
              <span className="hidden sm:block text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                All-in-one AI creative studio
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/use-cases" data-testid="marketing-usecases-link" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-muted-foreground hover:text-white">Use cases</Button>
            </Link>
            <Link to="/pricing" data-testid="marketing-pricing-link" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-muted-foreground hover:text-white">Pricing</Button>
            </Link>
            <Link to="/about" data-testid="marketing-about-link" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-muted-foreground hover:text-white">About</Button>
            </Link>
            <Link to="/blog" data-testid="marketing-blog-link" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-muted-foreground hover:text-white">Blog</Button>
            </Link>
            <Link to="/contact" data-testid="marketing-contact-link" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-muted-foreground hover:text-white">Contact</Button>
            </Link>
            <Link to="/login" data-testid="marketing-login-link" className="hidden sm:inline-flex">
              <Button variant="ghost" className="text-muted-foreground hover:text-white">Sign in</Button>
            </Link>
            <Link to="/register" data-testid="marketing-getstarted-link">
              <Button className="bg-primary text-black hover:bg-[#00C2D9] font-semibold rounded-full px-4 sm:px-5 h-9">
                Get started
              </Button>
            </Link>
            {/* Mobile menu toggle */}
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
              data-testid="marketing-mobile-menu-toggle"
              className="sm:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg border border-white/10 text-white/90 hover:bg-white/5 transition-colors"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              data-testid="marketing-mobile-menu"
              className="sm:hidden overflow-hidden border-t border-white/10 bg-[#050508]/95 backdrop-blur-xl"
            >
              <div className="px-6 py-3 flex flex-col">
                {[
                  { to: "/use-cases", label: "Use cases", testid: "marketing-mobile-nav-usecases" },
                  { to: "/pricing", label: "Pricing", testid: "marketing-mobile-nav-pricing" },
                  { to: "/about", label: "About", testid: "marketing-mobile-nav-about" },
                  { to: "/blog", label: "Blog", testid: "marketing-mobile-nav-blog" },
                  { to: "/contact", label: "Contact", testid: "marketing-mobile-nav-contact" },
                  { to: "/login", label: "Sign in", testid: "marketing-mobile-nav-login" },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={item.testid}
                    onClick={() => setMenuOpen(false)}
                    className="py-3 text-base font-medium text-white/80 hover:text-white border-b border-white/5 last:border-0 transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      <main className="pt-16">{children}</main>

      <MarketingFooter />
    </div>
  );
}

export function MarketingFooter() {
  return (
    <footer className="relative z-10 border-t border-white/10 pt-12 pb-8 px-4 sm:px-6" data-testid="marketing-footer">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-3">
              <img src="/nox-logo.png" alt="Nox AI" className="h-8 w-8 object-contain" />
              <span className="font-heading font-black text-lg">Nox<span className="text-primary"> AI</span></span>
            </Link>
            <p className="text-sm text-muted-foreground max-w-xs">
              Your all-in-one AI studio to create images, videos, voiceovers, music and more.
            </p>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-wider font-bold text-white mb-3">Use cases</h4>
            <ul className="space-y-2">
              {USE_CASES.map((u) => (
                <li key={u.slug}>
                  <Link to={`/use-cases/${u.slug}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    {u.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-wider font-bold text-white mb-3">Product</h4>
            <ul className="space-y-2">
              <li><Link to="/use-cases" className="text-sm text-muted-foreground hover:text-primary transition-colors">All use cases</Link></li>
              <li><Link to="/pricing" data-testid="footer-pricing-link" className="text-sm text-muted-foreground hover:text-primary transition-colors">Pricing</Link></li>
              <li><Link to="/blog" data-testid="footer-blog-link" className="text-sm text-muted-foreground hover:text-primary transition-colors">Blog</Link></li>
              <li><Link to="/contact" data-testid="footer-contact-link" className="text-sm text-muted-foreground hover:text-primary transition-colors">Contact us</Link></li>
              <li><Link to="/register" className="text-sm text-muted-foreground hover:text-primary transition-colors">Get started free</Link></li>
              <li><Link to="/login" className="text-sm text-muted-foreground hover:text-primary transition-colors">Sign in</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-wider font-bold text-white mb-3">Company</h4>
            <ul className="space-y-2">
              <li><Link to="/about" data-testid="footer-about-link" className="text-sm text-muted-foreground hover:text-primary transition-colors">About Nox AI</Link></li>
              <li><Link to="/contact" data-testid="footer-contact-link2" className="text-sm text-muted-foreground hover:text-primary transition-colors">Contact us</Link></li>
              <li><a href="https://noxaiapp.com" className="text-sm text-muted-foreground hover:text-primary transition-colors">noxaiapp.com</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} Nox AI — Your all-in-one AI creative studio</span>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-primary transition-colors">Terms of Service</Link>
            <button
              onClick={() => { try { localStorage.removeItem("nox_cookie_consent"); window.location.reload(); } catch {} }}
              className="hover:text-primary transition-colors cursor-pointer"
              data-testid="footer-cookie-settings"
            >
              Cookie Settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
